import { apiClient } from '../../services/apiClient';

export type ExerciseType = 'SHELL_CHALLENGE' | 'FILL_BLANK' | 'MULTIPLE_CHOICE' | 'OUTPUT_MATCH';
export type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';
export type ValidatorType = 'EXACT_MATCH' | 'PATTERN_MATCH' | 'CUSTOM_FUNCTION' | 'MULTIPLE_CHOICE';
export type ExerciseStatus = 'DRAFT' | 'PUBLISHED' | 'UNPUBLISHED';

export interface Course {
  id: string;
  title: string;
  description: string | null;
  instructorId: string;
  defaultCommandPolicy: Record<string, unknown> | null;
  retentionPeriodDays: number | null;
  createdAt: string;
}

export interface Chapter {
  id: string;
  title: string;
  orderIndex: number;
}

export interface ExerciseAuthoring {
  id: string;
  type: ExerciseType;
  title: string;
  description: string;
  points: number;
  difficulty: Difficulty;
  setupCommands: string[];
  validatorType: ValidatorType;
  validatorConfig: Record<string, unknown>;
  solutionExplanation: string | null;
  hints: string[];
  commandPolicy: Record<string, unknown>;
  status: ExerciseStatus;
}

export interface ExercisePreview {
  id: string;
  title: string;
  description: string;
  type: ExerciseType;
  points: number;
  hints: string | null;
  solutionExplanation: string | null;
  validatorType: ValidatorType;
  options: { id: string; text: string }[];
}

export interface ExerciseRequest {
  type: ExerciseType;
  title: string;
  description: string;
  points: number;
  difficulty: Difficulty;
  setupCommands: string[];
  validatorType: ValidatorType;
  validatorConfig: Record<string, unknown>;
  solutionExplanation: string;
  hints: string[];
  commandPolicy: Record<string, unknown>;
}

export function createCourse(
  title: string,
  description: string,
  defaultCommandPolicy?: Record<string, unknown>,
): Promise<{ courseId: string }> {
  return apiClient('/courses', {
    method: 'POST',
    body: JSON.stringify({ title, description, defaultCommandPolicy }),
  });
}

export function listCourses(): Promise<Course[]> {
  return apiClient('/courses');
}

export function getCourse(courseId: string): Promise<Course> {
  return apiClient(`/courses/${courseId}`);
}

export function enrollStudent(courseId: string, userId?: string): Promise<{ enrollmentId: string }> {
  return apiClient(`/courses/${courseId}/enroll`, {
    method: 'POST',
    body: JSON.stringify(userId ? { userId } : {}),
  });
}

export function listChapters(courseId: string): Promise<Chapter[]> {
  return apiClient(`/courses/${courseId}/chapters`);
}

export function createChapter(
  courseId: string,
  title: string,
  orderIndex: number,
): Promise<{ chapterId: string }> {
  return apiClient(`/courses/${courseId}/chapters`, {
    method: 'POST',
    body: JSON.stringify({ title, orderIndex }),
  });
}

export function listExercisesByChapter(chapterId: string): Promise<ExerciseAuthoring[]> {
  return apiClient(`/chapters/${chapterId}/exercises`);
}

export function createExercise(
  chapterId: string,
  request: ExerciseRequest,
): Promise<{ exerciseId: string }> {
  return apiClient(`/chapters/${chapterId}/exercises`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function updateExercise(exerciseId: string, request: ExerciseRequest): Promise<void> {
  return apiClient(`/exercises/${exerciseId}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

export function deleteExercise(exerciseId: string): Promise<void> {
  return apiClient(`/exercises/${exerciseId}`, { method: 'DELETE' });
}

export function publishExercise(exerciseId: string): Promise<void> {
  return apiClient(`/exercises/${exerciseId}/publish`, { method: 'POST' });
}

export function unpublishExercise(exerciseId: string): Promise<void> {
  return apiClient(`/exercises/${exerciseId}/unpublish`, { method: 'POST' });
}

export function previewExercise(exerciseId: string): Promise<ExercisePreview> {
  return apiClient(`/exercises/${exerciseId}/preview`);
}
